import { FC, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';

import styles from './StopDetails.module.scss';
import { TripStop } from '../../../../../types/trip.types';
import ActivityHeader from './components/ActivityHeader/ActivityHeader';
import ActivityInfo from './components/ActivityInfo/ActivityInfo';
import TravelContacts from './components/TravelContacts/TravelContacts';
import SmartTools from './components/SmartTools/SmartTools';
import DocumentsList from './components/DocumentsList/DocumentsList';
import CreateActivityModal from '../CreateActivityModal';
import CustomButton from '../../../../../common/CustomButton';
import ConfirmActionModal from '../../../../../common/ConfirmActionModal/ConfirmActionModal';
import { DeleteIcon, EditIcon } from '../../../../../components/Icons';
import { useAppSelector } from '../../../../../hooks/redux';
import { useDeleteTripStopMutation } from '../../../../../store/services/tripsApi';
import { useCurrencySymbol } from '../../../../../hooks/useCurrencySymbol';
import EventReminderSettings from '../../../../../common/EventReminderSettings/EventReminderSettings';
import { getStopEndDateTimeIso, getStopStartDateTimeIso } from '../../utils/activityFormatters';

interface StopDetailsProps {
  activity: TripStop;
  onClose: () => void;
  onShowFlightDelayPrediction?: (activityId: number) => void;
  onRefetchTrip?: () => void;
  onShowTravelContacts?: (activity: TripStop) => void;
  onShowGoNow?: (activity: TripStop) => void;
  onShowPackingList?: (activity: TripStop) => void;
  onShowAlternativeFlights?: (activity: TripStop) => void;
  tripId?: string;
  nestedModalZIndex?: number;
}

const StopDetails: FC<StopDetailsProps> = ({
  activity,
  onClose,
  onShowFlightDelayPrediction,
  onRefetchTrip,
  onShowTravelContacts,
  onShowGoNow,
  onShowPackingList,
  onShowAlternativeFlights,
  tripId,
  nestedModalZIndex,
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTripStop, { isLoading: isDeletingStop }] = useDeleteTripStopMutation();
  const user = useAppSelector(state => state.user);
  const currencySymbol = useCurrencySymbol();
  const stopStartIso = getStopStartDateTimeIso(activity);
  const stopEndIso = getStopEndDateTimeIso(activity);
  const skipReminders = !stopStartIso;
  const stopPastReferenceIso = stopEndIso || stopStartIso;
  const stopPastReferenceDateTime = stopPastReferenceIso ? new Date(stopPastReferenceIso) : null;
  const stopPastReferenceTime = stopPastReferenceDateTime?.getTime();
  const isStopInPast =
    typeof stopPastReferenceTime === 'number' && !Number.isNaN(stopPastReferenceTime)
      ? stopPastReferenceTime <= Date.now()
      : false;

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    onRefetchTrip?.();
    onClose();
  };

  const handleConfirmDelete = async () => {
    if (!tripId) return;

    try {
      await deleteTripStop({ id: activity.id, tripId }).unwrap();
      setShowDeleteConfirm(false);
      onRefetchTrip?.();
      onClose();
    } catch (error) {
      console.error('Failed to delete trip stop:', error);
    }
  };

  const handleTravelContacts = () => {
    if (onShowTravelContacts) {
      onShowTravelContacts(activity);
    }
  };

  const handleFlightDelayPrediction = () => {
    if (onShowFlightDelayPrediction) {
      onShowFlightDelayPrediction(activity.id);
    }
  };

  const handleGoNow = () => {
    if (onShowGoNow) {
      onShowGoNow(activity);
    }
  };

  const handlePackingList = () => {
    if (onShowPackingList) {
      onShowPackingList(activity);
    }
  };

  const handleAlternativeFlights = () => {
    if (onShowAlternativeFlights) {
      onShowAlternativeFlights(activity);
    }
  };

  const getActivityTitle = (): string => {
    try {
      switch (activity.stop_type) {
        case 'Flight':
          return `${activity.flight_from || 'Unknown'} → ${activity.flight_to || 'Unknown'}`;
        case 'BorderCrossing':
          return `${activity.country_from || 'Unknown'} → ${activity.country_to || 'Unknown'}`;
        case 'Transportation':
          return `${activity.vehicle_type || 'Transportation'}`;
        case 'Parking':
        case 'Directions':
          return activity.place || activity.stop_type;
        default:
          // @ts-expect-error - activity might have name or place based on type
          return activity.name || activity.place || activity.stop_type;
      }
    } catch (error) {
      console.warn('Error getting activity title:', error);
      return activity.stop_type;
    }
  };

  return (
    <>
      <Box className={styles.stopDetails}>
        <ActivityHeader onClose={onClose} />
        <Box className={styles.content}>
          <Box className={styles.activityTypeRow}>
            <Typography
              variant="h9Bold"
              sx={{ color: '#222B3A' }}
            >
              {activity.stop_type}
            </Typography>
            <Box className={styles.activityActions}>
              <CustomButton
                variant="outlined"
                onClick={handleEdit}
                customClass={styles.editButton}
                startIcon={<EditIcon />}
                textVariant="h11Bold"
              >
                Edit
              </CustomButton>
              <IconButton
                className={styles.deleteIconButton}
                onClick={() => setShowDeleteConfirm(true)}
                aria-label="Delete stop"
                disabled={isDeletingStop}
              >
                <DeleteIcon size={20} />
              </IconButton>
            </Box>
          </Box>

          <Box className={styles.stopReminderBar}>
            <EventReminderSettings
              scope="stop"
              stopId={activity.id}
              compact
              skip={skipReminders}
              eventStartsAtIso={stopStartIso}
            />
          </Box>
          <Box className={styles.titleSection}>
            <Typography
              variant="h8Bold"
              className={styles.activityTitle}
            >
              {getActivityTitle()}
            </Typography>
          </Box>
          <ActivityInfo activity={activity} />
          {activity.amount_spend > 0 && user.show_trip_budgets && (
            <Box className={styles.budgetSection}>
              <Typography
                variant="h10Semibold"
                className={styles.budgetText}
                sx={{ color: '#222B3A' }}
              >
                Total Budget: {currencySymbol}
                {activity.amount_spend}
              </Typography>
            </Box>
          )}

          {activity.notes && (
            <Box className={styles.notesSection}>
              <Typography
                variant="h10"
                className={styles.notesText}
              >
                {activity.notes}
              </Typography>
            </Box>
          )}
          <TravelContacts onClick={handleTravelContacts} />
          <SmartTools
            activityType={activity.stop_type}
            showGoNow={!isStopInPast}
            onFlightDelayPrediction={handleFlightDelayPrediction}
            onGoNow={handleGoNow}
            onPackingList={handlePackingList}
            onAlternativeFlights={onShowAlternativeFlights ? handleAlternativeFlights : undefined}
          />
          <DocumentsList
            tripStopId={activity.id}
            onRefetchTrip={onRefetchTrip}
          />
        </Box>
      </Box>
      <CreateActivityModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
        tripId={parseInt(tripId!)}
        editMode={true}
        activityData={activity}
        zIndex={nestedModalZIndex}
      />

      <ConfirmActionModal
        open={showDeleteConfirm}
        onClose={() => !isDeletingStop && setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        description={`You want to remove the stop “${getActivityTitle()}”?`}
        confirmLabel="Delete stop"
        isConfirming={isDeletingStop}
        zIndex={nestedModalZIndex}
      />
    </>
  );
};

export default StopDetails;
